/**
 * Economía DreamVinyl — Grooves (puntos de reputación / ranking).
 *
 * Uso previsto: Edge Functions, triggers SQL o servicio backend que llame a
 * `computeGrooveGrants` y aplique cada fila en `profiles` + `grooves_ledger`
 * con `idempotency_key` único (evita doble premio y abuso).
 *
 * No otorgar grooves desde el cliente con la anon key sin validación servidor.
 */

/** Tipos de envío / logística al cerrar una venta (no subasta). */
export const SHIPPING_KIND = {
  /** Envío físico entre usuarios (mensajería directa). */
  FISICO_USUARIO: 'fisico_usuario',
  /** Punto de recogida acordado. */
  PUNTO_RECOGIDA: 'punto_recogida',
  /** Envío / entrega vía partner verificador (valor interno `tienda_verificadora`). */
  TIENDA_VERIFICADORA: 'tienda_verificadora',
};

/** Grooves por venta cerrada según tipo de envío (comprador y vendedor reciben el mismo importe cada uno). */
export const GROOVES_SALE_CLOSED_BY_SHIPPING = {
  [SHIPPING_KIND.FISICO_USUARIO]: 1,
  [SHIPPING_KIND.PUNTO_RECOGIDA]: 3,
  [SHIPPING_KIND.TIENDA_VERIFICADORA]: 4,
};

/** Resto de reglas acordadas. */
export const GROOVES = {
  /** Tras valoración publicada (perfil, lugar, evento…). Receptor del premio: quien valora. */
  REVIEW_PUBLISHED: 2,
  /** Publicación de comunidad aceptada por moderación. */
  COMMUNITY_POST_ACCEPTED: 5,
  /** Cobro en grooves_partner al aprobar destacado en portada Comunidad (con solicitud partner; valida admin en cliente). */
  COMUNIDAD_DESTACAR_PARTNER: 150,
  /** Compra de entrada procesada por DreamVinyl. */
  TICKET_PURCHASE: 10,
  /** Subasta cerrada con éxito (una vez por rol). */
  AUCTION_SELLER: 5,
  AUCTION_BUYER: 5,
};

/** Costes de referencia en UI; el servidor lee `grooves_config` (config_kind = spend). */
export const AI_SCAN_COST = 25;
export const LISTING_FEATURE_COST = 75;
export const FILTER_SLOT_EXTRA_COST = 40;
export const SELLER_STATS_30D_COST = 120;
export const BASE_SAVED_FILTERS = 5;

/** Mensaje unificado cuando el usuario no tiene `phone_verified_at` (capa UI; el servidor también bloquea). */
export const GROOVES_PHONE_GATE_USER_MESSAGE = 'Verifica tu teléfono en Ajustes para activar tus Grooves';

/**
 * Saldo unificado para UI y comprobaciones en cliente.
 * `grooves_total ?? grooves` falla cuando `grooves_total` es `0` (no es nullish) y `grooves` tiene el valor real
 * (p. ej. edición manual de una sola columna o filas desincronizadas). Con columnas coherentes (ledger/triggers),
 * ambas coinciden y el resultado es el mismo número.
 * @param {{ grooves?: unknown, grooves_total?: unknown } | null | undefined} row
 */
export function profileGroovesBalance(row) {
  if (row == null || typeof row !== 'object') return 0;
  const t = Number(row.grooves_total);
  const g = Number(row.grooves);
  const nt = Number.isFinite(t) ? t : 0;
  const ng = Number.isFinite(g) ? g : 0;
  return Math.max(0, nt, ng);
}

/**
 * Grooves ganados por actividad (nivel / progresión). Excluye compras Stripe.
 * @param {{ grooves_earned_total?: unknown } | null | undefined} row
 */
export function profileGroovesEarnedTotal(row) {
  if (row == null || typeof row !== 'object') return 0;
  const e = Number(row.grooves_earned_total);
  return Number.isFinite(e) ? Math.max(0, e) : 0;
}

/**
 * Límites de política anti-abuso (el servidor debe aplicarlos antes de conceder).
 * Ejemplos: contar filas en `reviews` / `grooves_ledger` por ventana temporal.
 */
export const ANTI_ABUSE = {
  /** Máx. valoraciones que otorgan grooves al mismo objetivo (mismo tipo+id) por autor y día natural. */
  maxReviewGrantsPerAuthorPerTargetPerDay: 3,
  /** Máx. concesiones por publicaciones de comunidad aceptadas por autor y semana ISO. */
  maxCommunityPostGrantsPerAuthorPerWeek: 2,
  /** Máx. grooves por compra de entradas duplicada (mismo order_id). */
  maxTicketGrantsPerOrder: 1,
};

/** Motivos legibles para `grooves_ledger.reason` o logs. */
export const GROOVE_REASON = {
  SALE_CLOSED: 'venta_cerrada',
  AUCTION_CLOSED: 'subasta_cerrada',
  REVIEW: 'valoracion',
  COMMUNITY_POST: 'publicacion_comunidad',
  TICKET: 'entrada_dreamvinyl',
};

/** Etiquetas UI para reasons añadidos en fases Grooves 3–8. */
export const GROOVES_LEDGER_REASON_LABELS = {
  stripe_pack_purchase: 'Compra de Grooves (Stripe)',
  reto_nivel: 'Reto por nivel',
  devolucion_incidencia: 'Devolución / incidencia',
  inactividad_decadencia: 'Ajuste por inactividad prolongada',
  ai_scan: 'Escaneo IA de portada',
  comunidad_portada_destacada: 'Portada destacada Comunidad',
  destacar_listing: 'Destacar anuncio en marketplace',
  filtro_guardado_extra: 'Filtro guardado extra',
  estadisticas_vendedor_30d: 'Estadísticas premium vendedor (30 d)',
  badge_cosmetico_surco_dorado: 'Insignia: Surco dorado',
  badge_cosmetico_coleccionista_nocturno: 'Insignia: Coleccionista nocturno',
  badge_cosmetico_crate_digger: 'Insignia: Crate digger',
  badge_cosmetico_melomano_vintage: 'Insignia: Melómano vintage',
};

/**
 * Normaliza `transactions.shipping_type` (u otros textos legacy) al enum SHIPPING_KIND.
 * Valor por defecto conservador: envío físico entre usuarios.
 */
export function normalizeShippingType(raw) {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (!s) return SHIPPING_KIND.FISICO_USUARIO;
  if (['punto_recogida', 'pickup', 'pickup_point', 'recogida', 'punto_de_recogida'].includes(s)) {
    return SHIPPING_KIND.PUNTO_RECOGIDA;
  }
  if (
    ['tienda_verificadora', 'verified_store', 'store_verified', 'verificadora', 'tienda_verificada', 'verified'].includes(
      s
    )
  ) {
    return SHIPPING_KIND.TIENDA_VERIFICADORA;
  }
  if (
    [
      'fisico_usuario',
      'physical',
      'direct',
      'envio_directo',
      'envio_fisico',
      'user_to_user',
      'standard',
      'correo',
    ].includes(s)
  ) {
    return SHIPPING_KIND.FISICO_USUARIO;
  }
  return SHIPPING_KIND.FISICO_USUARIO;
}

export function groovesAmountForClosedSale(shippingKind) {
  return GROOVES_SALE_CLOSED_BY_SHIPPING[shippingKind] ?? GROOVES_SALE_CLOSED_BY_SHIPPING[SHIPPING_KIND.FISICO_USUARIO];
}

/** Umbrales de nivel según Grooves ganados por actividad (no compras Stripe). */
export function levelFromGroovesTotal(points) {
  const g = Number(points) || 0;
  if (g >= 2500) return { key: 'maestro', label: 'Maestro del Surco', border: '#c8a84b', glow: true };
  if (g >= 1000) return { key: 'crate', label: 'Crate Digger', border: '#c8a84b', glow: false };
  if (g >= 500) return { key: 'melo', label: 'Melómano', border: '#7b6ff0', glow: false };
  if (g >= 200) return { key: 'coleccionista', label: 'Coleccionista', border: '#5DCAA5', glow: false };
  return { key: 'oyente', label: 'Oyente', border: 'transparent', glow: false };
}

/** Surcos dorados iluminados en UI (1–5), alineado con los umbrales de `levelFromGroovesTotal`. */
export function groovesSurcoVisualCount(points) {
  const g = Number(points) || 0;
  if (g >= 2500) return 5;
  if (g >= 1000) return 4;
  if (g >= 500) return 3;
  if (g >= 200) return 2;
  return 1;
}

/**
 * @typedef {{ profileId: string, amount: number, idempotencyKey: string, reason: string, meta?: Record<string, unknown> }} GrooveGrant
 */

/**
 * Devuelve las concesiones a aplicar (sin escribir en BD).
 * Exclusión venta vs subasta: si `listingWasAuction` o `eventType === 'auction_completed'`,
 * no se mezclan premios de venta directa sobre el mismo hecho.
 *
 * @param {'sale_completed'|'auction_completed'|'review_published'|'community_post_accepted'|'ticket_purchased'} eventType
 * @param {Record<string, unknown>} payload
 * @returns {GrooveGrant[]}
 */
export function computeGrooveGrants(eventType, payload) {
  const p = payload || {};

  switch (eventType) {
    case 'sale_completed': {
      if (p.listingWasAuction === true || p.isAuction === true) return [];
      const txId = String(p.transactionId || '');
      const buyerId = String(p.buyerId || '');
      const sellerId = String(p.sellerId || '');
      if (!txId || !buyerId || !sellerId) return [];
      const kind = normalizeShippingType(p.shippingType);
      const amount = groovesAmountForClosedSale(kind);
      return [
        {
          profileId: buyerId,
          amount,
          idempotencyKey: `sale_closed:${txId}:buyer`,
          reason: GROOVE_REASON.SALE_CLOSED,
          meta: { shippingKind: kind, role: 'buyer' },
        },
        {
          profileId: sellerId,
          amount,
          idempotencyKey: `sale_closed:${txId}:seller`,
          reason: GROOVE_REASON.SALE_CLOSED,
          meta: { shippingKind: kind, role: 'seller' },
        },
      ];
    }
    case 'auction_completed': {
      const txId = String(p.transactionId || p.auctionId || '');
      const buyerId = String(p.buyerId || '');
      const sellerId = String(p.sellerId || '');
      if (!txId || !buyerId || !sellerId) return [];
      return [
        {
          profileId: sellerId,
          amount: GROOVES.AUCTION_SELLER,
          idempotencyKey: `auction_closed:${txId}:seller`,
          reason: GROOVE_REASON.AUCTION_CLOSED,
          meta: { role: 'seller' },
        },
        {
          profileId: buyerId,
          amount: GROOVES.AUCTION_BUYER,
          idempotencyKey: `auction_closed:${txId}:buyer`,
          reason: GROOVE_REASON.AUCTION_CLOSED,
          meta: { role: 'buyer' },
        },
      ];
    }
    case 'review_published': {
      const reviewId = String(p.reviewId || '');
      const reviewerId = String(p.reviewerProfileId || '');
      if (!reviewId || !reviewerId) return [];
      return [
        {
          profileId: reviewerId,
          amount: GROOVES.REVIEW_PUBLISHED,
          idempotencyKey: `review:${reviewId}:reviewer`,
          reason: GROOVE_REASON.REVIEW,
          meta: { targetType: p.targetType, targetId: p.targetId },
        },
      ];
    }
    case 'community_post_accepted': {
      const postId = String(p.postId || '');
      const authorId = String(p.authorProfileId || '');
      if (!postId || !authorId) return [];
      return [
        {
          profileId: authorId,
          amount: GROOVES.COMMUNITY_POST_ACCEPTED,
          idempotencyKey: `community_post:${postId}:author`,
          reason: GROOVE_REASON.COMMUNITY_POST,
          meta: {},
        },
      ];
    }
    case 'ticket_purchased': {
      const orderId = String(p.orderId || '');
      const buyerId = String(p.buyerProfileId || '');
      if (!orderId || !buyerId) return [];
      return [
        {
          profileId: buyerId,
          amount: GROOVES.TICKET_PURCHASE,
          idempotencyKey: `ticket_order:${orderId}:buyer`,
          reason: GROOVE_REASON.TICKET,
          meta: {},
        },
      ];
    }
    default:
      return [];
  }
}
